import { PROVIDER_IDS, type ProviderId, STORAGE_CLASSES } from './priceBook'
import {
  DEFAULT_SPEC,
  isCount,
  isValidDiscount,
  isValidGb,
  LIMITS,
  MEMORY_OPTIONS,
  NO_DISCOUNTS,
  OPERATING_SYSTEMS,
  PERIODS,
  type Resource,
  type Spec,
  VCPU_OPTIONS,
} from './spec'

/**
 * Share links.
 *
 * Same contract as the other tools: the whole design lives in the URL fragment
 * (`#…`), which browsers never send to a server, and the payload is versioned.
 * Fields are `key=value` pairs, and a missing field takes its default:
 *
 *   #v1;r=vm:4:16:linux:24:2,db:2:8:100:single:1,disk:100:2,bucket:500:standard,lb:1;
 *    in=100;out=1000;d=azure:15;p=month
 *
 * Each resource is its kind followed by its fields, in a fixed order per kind:
 *
 *   vm:<vCPUs>:<GB memory>:<linux|windows>:<hours a day>:<count>
 *   db:<vCPUs>:<GB memory>:<GB storage>:<single|ha>:<count>
 *   disk:<GB>:<count>
 *   bucket:<GB>:<standard|infrequent|archive>
 *   lb:<count>
 *
 * Discounts list only the providers that have one.
 */
const VERSION = 'v1'

export function encodeSpec(spec: Spec): string {
  const discounts = PROVIDER_IDS.filter((id) => spec.discounts[id] !== 0).map(
    (id) => `${id}:${spec.discounts[id]}`,
  )
  const fields: [string, string][] = [
    ['r', spec.resources.map(encodeResource).join(',')],
    ['in', String(spec.transfer.inboundGb)],
    ['out', String(spec.transfer.outboundGb)],
    ...(discounts.length > 0 ? [['d', discounts.join(',')] as [string, string]] : []),
    ['p', spec.period],
  ]
  return [VERSION, ...fields.map(([key, value]) => `${key}=${value}`)].join(';')
}

function encodeResource(resource: Resource): string {
  switch (resource.kind) {
    case 'vm':
      return join(
        'vm',
        resource.vcpu,
        resource.memoryGb,
        resource.os,
        resource.hoursPerDay,
        resource.count,
      )
    case 'db':
      return join(
        'db',
        resource.vcpu,
        resource.memoryGb,
        resource.storageGb,
        resource.ha ? 'ha' : 'single',
        resource.count,
      )
    case 'disk':
      return join('disk', resource.sizeGb, resource.count)
    case 'bucket':
      return join('bucket', resource.sizeGb, resource.storageClass)
    case 'lb':
      return join('lb', resource.count)
  }
}

const join = (...parts: (string | number)[]): string => parts.join(':')

/**
 * Returns `null` for anything unrecognised — a bad link must land the visitor on
 * a working calculator, never on an error page or a half-applied design.
 */
export function decodeSpec(payload: string): Spec | null {
  const text = payload.replace(/^#/, '')
  if (text === '') return null

  const [version, ...pairs] = text.split(';')
  if (version !== VERSION) return null

  const fields = new Map<string, string>()
  for (const pair of pairs) {
    const at = pair.indexOf('=')
    if (at <= 0) return null
    fields.set(pair.slice(0, at), pair.slice(at + 1))
  }

  const resources = field(fields, 'r', DEFAULT_SPEC.resources, decodeResources)
  const inboundGb = field(fields, 'in', DEFAULT_SPEC.transfer.inboundGb, decodeGb)
  const outboundGb = field(fields, 'out', DEFAULT_SPEC.transfer.outboundGb, decodeGb)
  const discounts = field(fields, 'd', NO_DISCOUNTS, decodeDiscounts)
  const period = field(fields, 'p', DEFAULT_SPEC.period, (value) => oneOf(PERIODS, value))

  if (
    resources === undefined ||
    inboundGb === undefined ||
    outboundGb === undefined ||
    discounts === undefined ||
    period === undefined
  ) {
    return null
  }
  return { resources, transfer: { inboundGb, outboundGb }, discounts, period }
}

/** Absent → the default; present but invalid → `undefined`, which rejects the link. */
function field<T>(
  fields: Map<string, string>,
  key: string,
  fallback: T,
  parse: (value: string) => T | undefined,
): T | undefined {
  const value = fields.get(key)
  return value === undefined ? fallback : parse(value)
}

function decodeResources(text: string): Resource[] | undefined {
  // An empty list is a valid design: only the data transfer is left to price.
  if (text === '') return []
  const items = text.split(',')
  if (items.length > LIMITS.resources) return undefined
  const resources: Resource[] = []
  for (const [index, item] of items.entries()) {
    const resource = decodeResource(item, index + 1)
    if (!resource) return undefined
    resources.push(resource)
  }
  return resources
}

function decodeResource(text: string, id: number): Resource | undefined {
  const [kind, ...parts] = text.split(':')
  const count = (value: string | undefined): number | undefined =>
    decodeCount(value, LIMITS.count, 1)

  if (kind === 'vm' && parts.length === 5) {
    const [vcpuText, memoryText, osText, hoursText, countText] = parts
    const vcpu = decodeOption(VCPU_OPTIONS, vcpuText)
    const memoryGb = decodeOption(MEMORY_OPTIONS, memoryText)
    const os = oneOf(OPERATING_SYSTEMS, osText)
    const hoursPerDay = decodeCount(hoursText, LIMITS.hoursPerDay, LIMITS.minHoursPerDay)
    const n = count(countText)
    if (vcpu === undefined || memoryGb === undefined || !os) return undefined
    if (hoursPerDay === undefined || n === undefined) return undefined
    return { id, kind, vcpu, memoryGb, os, hoursPerDay, count: n }
  }
  if (kind === 'db' && parts.length === 5) {
    const [vcpuText, memoryText, storageText, haText, countText] = parts
    const vcpu = decodeOption(VCPU_OPTIONS, vcpuText)
    const memoryGb = decodeOption(MEMORY_OPTIONS, memoryText)
    const storageGb = decodeGb(storageText)
    const n = count(countText)
    if (vcpu === undefined || memoryGb === undefined || storageGb === undefined) return undefined
    if ((haText !== 'ha' && haText !== 'single') || n === undefined) return undefined
    return { id, kind, vcpu, memoryGb, storageGb, ha: haText === 'ha', count: n }
  }
  if (kind === 'disk' && parts.length === 2) {
    const sizeGb = decodeGb(parts[0])
    const n = count(parts[1])
    if (sizeGb === undefined || n === undefined) return undefined
    return { id, kind, sizeGb, count: n }
  }
  if (kind === 'bucket' && parts.length === 2) {
    const sizeGb = decodeGb(parts[0])
    const storageClass = oneOf(STORAGE_CLASSES, parts[1])
    if (sizeGb === undefined || !storageClass) return undefined
    return { id, kind, sizeGb, storageClass }
  }
  if (kind === 'lb' && parts.length === 1) {
    const n = count(parts[0])
    return n === undefined ? undefined : { id, kind, count: n }
  }
  return undefined
}

function decodeDiscounts(text: string): Record<ProviderId, number> | undefined {
  const discounts: Record<ProviderId, number> = { ...NO_DISCOUNTS }
  for (const item of text.split(',')) {
    const [id, value, ...rest] = item.split(':')
    const provider = oneOf(PROVIDER_IDS, id)
    // Number('') is 0, which would turn an empty value into a valid discount.
    const percent = value === undefined || value.trim() === '' ? NaN : Number(value)
    if (!provider || rest.length > 0 || !isValidDiscount(percent)) return undefined
    discounts[provider] = percent
  }
  return discounts
}

function decodeCount(text: string | undefined, max: number, min = 0): number | undefined {
  // Digits only: Number('') is 0 and Number('1e3') is 1000.
  if (text === undefined || !/^\d+$/.test(text)) return undefined
  const value = Number(text)
  return isCount(value, max, min) ? value : undefined
}

function decodeGb(text: string | undefined): number | undefined {
  const value = decodeCount(text, LIMITS.gb)
  return value !== undefined && isValidGb(value) ? value : undefined
}

function decodeOption(options: readonly number[], text: string | undefined): number | undefined {
  const value = decodeCount(text, Number.MAX_SAFE_INTEGER)
  return value !== undefined && options.includes(value) ? value : undefined
}

function oneOf<T extends string>(options: readonly T[], value: string | undefined): T | undefined {
  return options.find((option) => option === value)
}

/** Writes the spec into the fragment without adding a history entry per change. */
export function replaceLocationSpec(spec: Spec): void {
  if (typeof window === 'undefined') return
  const next = `${window.location.pathname}${window.location.search}#${encodeSpec(spec)}`
  window.history.replaceState(null, '', next)
}

export function readLocationSpec(): Spec | null {
  if (typeof window === 'undefined') return null
  return decodeSpec(window.location.hash)
}
